/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import MultiSelectCheckbox from '../form/MultiSelectCheckbox';
import TextArea from '../form/TextArea';
import BottomBar from '../BottomBar';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { COUNTRY } from '../../../environment';
import {
  updateDesignations,
  updateAdditionalCertifications,
} from '../../../actions/preferences';

const ProfessionalDetails2 = ({
  designations,
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  errorCallback,
  currentStep,
  previousPage,
  draftValues,
  onDraftChange,
}) => {
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    defaultValues: draftValues || {
      designations: [],
      additional_designations: '',
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const onSubmit = async values => {
    // Validation: require at least one designation or additional designation
    if (!values.designations || values.designations.length === 0) {
      setError('designations', {
        type: 'manual',
        message: (
          <FormattedMessage defaultMessage="Must select at least one designation" />
        ),
      });
      return;
    }
    clearErrors('formError');

    const errorMessage = (
      <FormattedMessage defaultMessage="There was a problem updating your professional details." />
    );

    const designationValues = {
      designations: values.designations,
    };

    const additionalDesignationValues = {
      additional_designations: {
        certifications:
          values.additional_designations &&
          values.additional_designations.split(','),
      },
      type_of: 'designations',
    };

    openLoadingPanel(
      <FormattedMessage defaultMessage="Updating professional details..." />,
    );

    try {
      await Promise.all([
        dispatch(updateDesignations(designationValues)),
        dispatch(updateAdditionalCertifications(additionalDesignationValues)),
      ]);

      closeLoadingPanel();
      nextPage();
    } catch (error) {
      errorCallback(error, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>
          <FormattedMessage defaultMessage="Professional Details" />
        </h1>
        <div className="sub-page-indicator">
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {`2/${COUNTRY === 'US' ? '4' : '3'}`}
        </div>
      </div>

      <h3 style={{ marginBottom: '20px' }}>
        <FormattedMessage defaultMessage="Designation (select all that apply)" />
      </h3>

      <div className="input-row">
        <Controller
          name="designations"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              options={designations}
              error={errors.designations?.message}
            />
          )}
        />
      </div>

      <p className="onboarding-label">
        <FormattedMessage defaultMessage="Other Designations (separate by comma)" />
      </p>
      <div className="input-row">
        <Controller
          name="additional_designations"
          control={control}
          render={({ field }) => <TextArea {...field} />}
        />
      </div>

      <BottomBar
        openLoadingPanel={openLoadingPanel}
        closeLoadingPanel={closeLoadingPanel}
        nextPage={nextPage}
        currentStep={currentStep}
        previousPage={previousPage}
      />
    </form>
  );
};

ProfessionalDetails2.propTypes = {
  designations: PropTypes.array.isRequired,
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default ProfessionalDetails2;
